'use client'

import * as React from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { Languages } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const languages = [
    { code: 'ar', name: 'العربية', dir: 'rtl' },
    { code: 'en', name: 'English', dir: 'ltr' },
    { code: 'fr', name: 'Français', dir: 'ltr' },
]

export function LanguageSwitcher({ locale }: { locale: string }) {
    const router = useRouter()
    const pathname = usePathname()

    const switchLanguage = (newLocale: string) => {
        // e.g. /en/dashboard -> /ar/dashboard
        // We assume the first segment is the locale.
        const segments = pathname.split('/')
        // segments[0] is empty string because path starts with /
        // segments[1] is the locale (en, ar, fr)
        if (segments.length >= 2) {
            segments[1] = newLocale
        } else {
            // Edge case: root path /
            // Logic might depend on middleware, but generally /ar or /en
            segments[1] = newLocale // This might result in /ar if path was just /
        }

        const newPath = segments.join('/')
        router.push(newPath)
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md">
                    <Languages className="h-5 w-5" />
                    <span className="sr-only">Toggle language</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                {languages.map((lang) => (
                    <DropdownMenuItem
                        key={lang.code}
                        onClick={() => switchLanguage(lang.code)}
                        className={`gap-2 cursor-pointer ${locale === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'}`}
                    >
                        <span className="">{lang.name}</span>
                        {locale === lang.code && (
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-600"></span>
                            </span>
                        )}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
